import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, Check, ChevronLeft, ChevronRight, Copy, Download, MapPin, X } from 'lucide-react';
import { formatBytes, formatDateTime, mapUrl } from '../lib/admin';
import { cx, Eyebrow, FOCUS, IconButton, Segmented } from './admin/ui';

const valueText = (value) => (typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value));

// One line of the catalogue record
export const Row = ({ label, children, mono }) => (
  <div className="grid grid-cols-[118px_1fr] gap-4 border-b border-umber/10 py-2.5 last:border-b-0">
    <span className="pt-[3px] font-mono text-[10px] uppercase tracking-[0.18em] text-umber/45">{label}</span>
    <span className={cx('min-w-0 break-words text-[13.5px] text-ink', mono && 'font-mono text-[12px]')}>
      {children === undefined || children === null || children === '' ? <span className="text-umber/35">—</span> : children}
    </span>
  </div>
);

const NavButton = ({ onClick, side, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cx(
      'absolute top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full border border-parchment/20 text-parchment/80 transition-colors hover:bg-parchment hover:text-ink',
      side === 'left' ? 'left-4' : 'right-4', FOCUS,
    )}
  >
    {children}
  </button>
);

// Full-screen record of one photo with every piece of metadata we have
export default function PhotoDetail({ photo, onClose, onPrev, onNext, position, contributorLabel = 'Usuario', children }) {
  const [tab, setTab] = useState('general');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft' && onPrev) onPrev();
      if (event.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  if (!photo) return null;
  const gps = photo.gps;
  const metadata = photo.metadata || {};
  const exif = photo.exif || {};
  const exifEntries = Object.entries(exif).filter(([key]) => key !== 'GPS');
  const exifGps = exif.GPS ? Object.entries(exif.GPS) : [];
  const device = metadata.device || {};

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify({ ...photo, url: undefined, thumb_url: undefined }, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] flex font-ui text-ink bg-ink/80 backdrop-blur-[2px] md:p-6" onMouseDown={onClose}>
      <div
        className="mx-auto flex w-full max-w-[1400px] flex-col overflow-hidden border-umber/30 bg-vellum md:flex-row md:rounded-2xl md:border"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Image */}
        <div className="relative flex h-[42vh] flex-shrink-0 items-center justify-center bg-[#15100c] md:h-auto md:flex-1">
          <img src={photo.url} alt="" className="max-h-full max-w-full object-contain md:max-h-[calc(100vh-48px)]" />
          {onPrev && <NavButton side="left" onClick={onPrev}><ChevronLeft className="h-5 w-5" strokeWidth={1.5} /></NavButton>}
          {onNext && <NavButton side="right" onClick={onNext}><ChevronRight className="h-5 w-5" strokeWidth={1.5} /></NavButton>}
          {position && (
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-ink/70 px-3 py-1 font-mono text-[11px] tabular-nums text-parchment/70">
              {position}
            </span>
          )}
        </div>

        {/* Record */}
        <div className="flex min-h-0 w-full flex-1 flex-col md:w-[440px] md:flex-none md:border-l md:border-umber/15">
          <div className="flex items-start justify-between gap-3 border-b border-umber/10 px-6 pb-4 pt-5">
            <div className="min-w-0">
              <Eyebrow>Registro de foto</Eyebrow>
              <p className="mt-1 truncate font-serif text-[26px] leading-tight text-ink">{photo.folder_name || photo.folder || 'Sin folder'}</p>
              <p className="mt-0.5 truncate font-mono text-[11px] text-umber/45">{photo.id}</p>
            </div>
            <div className="flex flex-shrink-0 items-center">
              <a href={photo.url} target="_blank" rel="noreferrer" title="Original en calidad completa"
                className={cx('inline-flex h-10 w-10 items-center justify-center rounded-lg text-umber/70 transition-colors hover:bg-umber/[0.06] hover:text-ink', FOCUS)}>
                <Download className="h-4 w-4" strokeWidth={1.75} />
              </a>
              <IconButton icon={copied ? Check : Copy} label="Copiar metadata (JSON)" onClick={copyJson} />
              <IconButton icon={X} label="Cerrar" onClick={onClose} />
            </div>
          </div>

          <div className="px-6 pt-4">
            <Segmented
              size="sm"
              value={tab}
              onChange={setTab}
              options={[
                { value: 'general', label: 'General' },
                { value: 'metadata', label: 'Metadata app' },
                { value: 'exif', label: 'EXIF' },
              ]}
            />
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3">
            {tab === 'general' && (
              <>
                {gps ? (
                  <a href={mapUrl(gps)} target="_blank" rel="noreferrer"
                    className={cx('group mb-3 mt-1 flex items-center gap-3 rounded-xl border border-umber/15 px-4 py-3 transition-colors hover:border-umber/40', FOCUS)}>
                    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-ink text-parchment">
                      <MapPin className="h-4 w-4" strokeWidth={1.75} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-mono text-[12.5px] tabular-nums text-ink">{gps.latitude.toFixed(6)}, {gps.longitude.toFixed(6)}</span>
                      <span className="block text-xs text-umber/55">
                        {gps.accuracy ? `± ${Math.round(gps.accuracy)} m` : 'Precisión desconocida'}
                        {gps.altitude !== null && gps.altitude !== undefined ? ` · ${Math.round(gps.altitude)} m s. n. m.` : ''}
                        {gps.source ? ` · ${gps.source === 'app' ? 'GPS de la app' : 'EXIF'}` : ''}
                      </span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-umber/40 transition-colors group-hover:text-ink" strokeWidth={1.75} />
                  </a>
                ) : (
                  <div className="mb-3 mt-1 rounded-xl border border-dashed border-umber/20 px-4 py-3 text-xs text-umber/55">
                    Sin ubicación{metadata.location_status ? ` · permiso: ${metadata.location_status}` : ''}
                  </div>
                )}
                <Row label={contributorLabel}>{photo.user_email || photo.contributor}</Row>
                <Row label="Región">{photo.region}</Row>
                <Row label="Folder">{photo.folder_name || photo.folder}</Row>
                <Row label="Tomada">{photo.taken_at ? formatDateTime(photo.taken_at) : null}</Row>
                <Row label="Subida">{photo.uploaded_at ? formatDateTime(photo.uploaded_at) : null}</Row>
                <Row label="Zona horaria">{metadata.timezone_name ? `${metadata.timezone_name} (UTC${metadata.timezone_offset_minutes >= 0 ? '+' : ''}${(metadata.timezone_offset_minutes || 0) / 60})` : null}</Row>
                <Row label="Cámara">{photo.camera}</Row>
                <Row label="Dispositivo">{photo.device || device.model}</Row>
                <Row label="App">{metadata.app_version}</Row>
                <Row label="Resolución">{photo.width && photo.height ? `${photo.width} × ${photo.height} px` : null}</Row>
                <Row label="Tamaño">{photo.size_bytes ? formatBytes(photo.size_bytes) : null}</Row>
                {children}
              </>
            )}
            {tab === 'metadata' && (
              Object.keys(metadata).length
                ? Object.entries(metadata).map(([key, value]) => <Row key={key} label={key} mono>{valueText(value)}</Row>)
                : <p className="py-8 text-center text-sm text-umber/45">La app no envió metadata para esta foto.</p>
            )}
            {tab === 'exif' && (
              exifEntries.length || exifGps.length ? (
                <>
                  {exifEntries.map(([key, value]) => <Row key={key} label={key} mono>{valueText(value)}</Row>)}
                  {exifGps.map(([key, value]) => <Row key={`gps-${key}`} label={`GPS ${key}`} mono>{valueText(value)}</Row>)}
                </>
              ) : <p className="py-8 text-center text-sm text-umber/45">Sin EXIF todavía: se procesa unos segundos después de subir.</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
